import { useCallback, useEffect, useRef } from 'react';

type RunPageTreeRequest = <T>(
	runFetch: () => Promise<T>,
	onSuccess: (result: T) => void | Promise<unknown>,
	onError: (error: Error) => void | Promise<unknown>,
) => Promise<void>;

/**
 * Share one pending operation between automatic loading and Retry for the current scope.
 * This is a concurrency/stale-result guard, not a retry policy: transport retries are unchanged,
 * and callers must consume automatic triggers so a settled failure is not dispatched again.
 */
export function usePageTreeRequest(scope: string): RunPageTreeRequest {
	const activeRequest = useRef<object | null>(null);
	const activeScope = useRef(scope);
	const mounted = useRef(false);

	useEffect(() => {
		if (activeScope.current !== scope) {
			// Navigation or a different explicit refresh can start immediately. The old network
			// request is not cancelled, but its token no longer owns callbacks or guard cleanup.
			activeRequest.current = null;
			activeScope.current = scope;
		}
		mounted.current = true;
		return () => {
			mounted.current = false;
			// Preserve the guard across StrictMode's effect replay. Real unmounts
			// still ignore completions, and a different scope invalidates it above.
		};
	}, [scope]);

	return useCallback(
		async <T>(
			runFetch: () => Promise<T>,
			onSuccess: (result: T) => void | Promise<unknown>,
			onError: (error: Error) => void | Promise<unknown>,
		): Promise<void> => {
			if (!mounted.current || activeRequest.current) {
				return;
			}
			// Claim synchronously, before fetch can change state and cause a render or another click.
			// Token identity also distinguishes an old completion from a newer pending operation.
			const request = {};
			activeRequest.current = request;
			const isCurrent = () => mounted.current && activeRequest.current === request;
			try {
				const result = await runFetch();
				if (isCurrent()) {
					await onSuccess(result);
				}
			} catch (error) {
				if (isCurrent()) {
					await onError(error instanceof Error ? error : new Error(String(error)));
				}
			} finally {
				// Release on success or failure, but never let a stale request unlock a newer one.
				if (isCurrent()) {
					activeRequest.current = null;
				}
			}
		},
		[],
	);
}
